/* eslint-disable @wyz/module/no-relative-import */
import {
  Card,
  Flex,
  Icon,
  Switch,
  Text,
  Title,
  useConfirmation,
} from '@wyz/ui';
import { useBoolean } from '@wyz/utils';
import clsx from 'clsx';
import * as React from 'react';
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { Trans, useI18n } from '~/common/context';
import {
  REDISTRIBUTION_RULE_STATUSES,
  RedistributionRuleStatus,
} from '~/connect/schemas';
import { IRedistributionBusinessRulesForm } from '~/connect/types';
import './card.scss';
import {
  REDISTRIBUTION_BUSINESS_RULES,
  RedistributionBusinessRuleNode,
} from './rules';

type RuleChangeHandler = (
  field: keyof IRedistributionBusinessRulesForm,
  status: RedistributionRuleStatus,
) => void;

type Props = {
  name?: string;
  disabled?: boolean;
  // Supplier templates derive the supplier-backed statuses from their supplier list and pass them
  // in. The generic rule has no suppliers, so it reads them straight from the form.
  statuses?: IRedistributionBusinessRulesForm;
  // Supplier templates use this to cascade the rule down to their suppliers. The generic rule has
  // no suppliers and leaves it unset.
  onRuleChange?: RuleChangeHandler;
};

type RowProps = {
  rule: RedistributionBusinessRuleNode;
  name: string;
  disabled: boolean;
  status: RedistributionRuleStatus;
  onEnable?: () => void;
  onRuleChange?: RuleChangeHandler;
  children?: React.ReactNode;
};

const BusinessRuleRow = ({
  rule,
  name,
  disabled,
  status,
  onEnable,
  onRuleChange,
  children,
}: RowProps) => {
  const [expanded, expandedHandlers] = useBoolean(true);
  const { control } = useFormContext();
  const { t } = useI18n();
  const ask = useConfirmation();
  // An intermediate parent still has meaningful sub-rules: that is what makes it intermediate.
  const hasChildren =
    Boolean(rule.children?.length) &&
    status !== REDISTRIBUTION_RULE_STATUSES.DISABLED;

  const applyRuleChange = (
    field: ControllerRenderProps,
    next: RedistributionRuleStatus,
  ) => {
    field.onChange(next);
    onRuleChange?.(rule.field, next);
    if (next === REDISTRIBUTION_RULE_STATUSES.ENABLED) onEnable?.();
  };

  const handleOnChange = (
    field: ControllerRenderProps,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const next = event.target.checked
      ? REDISTRIBUTION_RULE_STATUSES.ENABLED
      : REDISTRIBUTION_RULE_STATUSES.DISABLED;

    if (
      next === REDISTRIBUTION_RULE_STATUSES.DISABLED &&
      rule.disableConfirmation &&
      status === REDISTRIBUTION_RULE_STATUSES.ENABLED
    ) {
      ask({
        size: 'lg',
        title: t(rule.disableConfirmation.titleKey),
        description: (
          <span className='text-wrap'>
            {t(rule.disableConfirmation.messageKey)}
          </span>
        ),
        cancelBtnText: t('app.common.button.cancel'),
        confirmBtnText: t('app.common.button.confirm'),
      }).onConfirm(() => applyRuleChange(field, next));
    } else {
      applyRuleChange(field, next);
    }
  };

  return (
    <div
      className={clsx('business-rule-row', {
        'business-rule-row--enabled':
          status === REDISTRIBUTION_RULE_STATUSES.ENABLED,
        'business-rule-row--intermediate':
          status === REDISTRIBUTION_RULE_STATUSES.INTERMEDIATE,
      })}
    >
      <Flex justifyContent='between' alignItems='start'>
        <Flex alignItems='center' className='space-x-2'>
          <Controller
            control={control}
            name={`${name}.${rule.field}`}
            render={({ field }) => (
              <Switch
                id={`${name}.${rule.field}`}
                name={`${name}.${rule.field}`}
                size='md'
                disabled={disabled}
                checked={status === REDISTRIBUTION_RULE_STATUSES.ENABLED}
                indeterminate={
                  status === REDISTRIBUTION_RULE_STATUSES.INTERMEDIATE
                }
                onChange={(event) => handleOnChange(field, event)}
              />
            )}
          />
          <label htmlFor={`${name}.${rule.field}`} className='mb-0'>
            <Text
              component='span'
              heading='normal'
              weight='semi-bold'
              color='tertiary'
            >
              <Trans i18nKey={rule.labelKey} />
            </Text>
            <Text heading='xs' color='secondary-gray'>
              <Trans i18nKey={rule.descriptionKey} />
            </Text>
          </label>
        </Flex>
        {hasChildren && (
          <div
            id={`toggle-${rule.field}`}
            role='button'
            className='business-rule-row__chevron'
            onClick={expandedHandlers.toggle}
          >
            <Icon size='md' icon={expanded ? 'arrow-down' : 'arrow-right'} />
          </div>
        )}
      </Flex>
      {hasChildren && expanded && (
        <div className='business-rule-row__children'>{children}</div>
      )}
    </div>
  );
};

const RedistributionBusinessRulesCard = ({
  name = 'businessRules',
  disabled = false,
  onRuleChange,
  statuses,
}: Props) => {
  const { control, setValue } = useFormContext();
  const businessRules = useWatch({
    name,
    control,
  }) as IRedistributionBusinessRulesForm | undefined;

  const statusOf = React.useCallback(
    (rule: RedistributionBusinessRuleNode): RedistributionRuleStatus =>
      (statuses ?? businessRules)?.[rule.field] ??
      REDISTRIBUTION_RULE_STATUSES.DISABLED,
    [businessRules, statuses],
  );

  React.useEffect(() => {
    // Sub-rules are meaningless without their parent, and the API rejects the payload when one of
    // them stays on while the parent is off.
    REDISTRIBUTION_BUSINESS_RULES.forEach((rule) => {
      if (
        businessRules?.[rule.field] !== REDISTRIBUTION_RULE_STATUSES.DISABLED
      ) {
        return;
      }

      rule.children?.forEach((child) => {
        setValue(
          `${name}.${child.field}`,
          REDISTRIBUTION_RULE_STATUSES.DISABLED,
        );
      });
    });
  }, [businessRules, name, setValue]);

  const disableSiblings = (
    parent: RedistributionBusinessRuleNode,
    enabledChild: RedistributionBusinessRuleNode,
  ) => {
    parent.children
      ?.filter((sibling) => sibling.field !== enabledChild.field)
      .forEach((sibling) =>
        setValue(
          `${name}.${sibling.field}`,
          REDISTRIBUTION_RULE_STATUSES.DISABLED,
        ),
      );
  };

  const activatedCount = REDISTRIBUTION_BUSINESS_RULES.filter(
    (rule) => statusOf(rule) !== REDISTRIBUTION_RULE_STATUSES.DISABLED,
  ).length;

  return (
    <Card className='mb-section'>
      <Card.Header className='pb-0'>
        <Title heading='lg' color='tertiary' weight='bold'>
          <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
          <span className='ms-2'>
            <Trans i18nKey='app.redistribution.business_rules.title' />
          </span>
        </Title>
      </Card.Header>
      <Card.Body>
        <Text heading='normal' weight='semi-bold' color='secondary'>
          <Trans
            i18nKey='app.redistribution.business_rules.activated_count'
            values={{ count: activatedCount }}
          />
        </Text>
        <div className='d-flex flex-column gap-2 mt-3'>
          {REDISTRIBUTION_BUSINESS_RULES.map((rule) => (
            <BusinessRuleRow
              key={rule.field}
              rule={rule}
              name={name}
              disabled={disabled}
              status={statusOf(rule)}
              onRuleChange={onRuleChange}
            >
              <div className='d-flex flex-column'>
                {rule.children?.map((child) => (
                  <BusinessRuleRow
                    key={child.field}
                    rule={child}
                    name={name}
                    disabled={disabled}
                    status={statusOf(child)}
                    onRuleChange={onRuleChange}
                    onEnable={
                      rule.exclusiveChildren
                        ? () => disableSiblings(rule, child)
                        : undefined
                    }
                  />
                ))}
              </div>
            </BusinessRuleRow>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

export default RedistributionBusinessRulesCard;
